/**
 * Substack-style Turning Point reading.
 *
 * Composes a written reading from the deterministic Life Clock state plus the
 * transcribed card text in `phase-text.js`. Pure function: no AI, no network,
 * no async — the same inputs always produce the same reading.
 */

import {
  describeDayDistance,
  formatDate,
  WAVE_WINDOW_DAYS,
} from "./life-clock.js";
import { getChallengePeriodText, getChallengePeriodName, getPhaseText } from "./phase-text.js";

/**
 * Phase graphic per sign — the JPG artwork in `/images` (Ari, Tau, Gem, Can,
 * Leo, Vir, Lib, Sco, Sag, Cap, Aqu, Pis). Resolved through Vite so the file is
 * fingerprinted into the build and served from `dist/assets`.
 */
const PHASE_GRAPHICS = {
  Aries: new URL("../../images/Ari.jpg", import.meta.url).href,
  Taurus: new URL("../../images/Tau.jpg", import.meta.url).href,
  Gemini: new URL("../../images/Gem.jpg", import.meta.url).href,
  Cancer: new URL("../../images/Can.jpg", import.meta.url).href,
  Leo: new URL("../../images/Leo.jpg", import.meta.url).href,
  Virgo: new URL("../../images/Vir.jpg", import.meta.url).href,
  Libra: new URL("../../images/Lib.jpg", import.meta.url).href,
  Scorpio: new URL("../../images/Sco.jpg", import.meta.url).href,
  Sagittarius: new URL("../../images/Sag.jpg", import.meta.url).href,
  Capricorn: new URL("../../images/Cap.jpg", import.meta.url).href,
  Aquarius: new URL("../../images/Aqu.jpg", import.meta.url).href,
  Pisces: new URL("../../images/Pis.jpg", import.meta.url).href,
};

/** "Within the three-week mark" — when the build-up to a turning point begins. */
const APPROACHING_WINDOW_DAYS = 21;

/**
 * Two questions per Challenge Period — every period raises its own issues
 * inside the Phase's theme, so the questions move with the period you are in.
 */
const STAGE_QUESTION_BUILDERS = [
  // First: residuals of the old phase meeting the new one, and its first seeds.
  ({ sign, focus }) => [
    `What is still unfinished from the phase you have just left, and how is it meeting the new demands of the ${sign} Phase — ${focus}?`,
    `Where are the first signs of this phase showing up in your actual life, and what in the ${focus} area is asking for your attention?`,
  ],
  // Second: the clues and the friction that show what will have to change.
  ({ sign, focus }) => [
    `What has begun to repeat, rub or cost you in the area of ${focus} — the friction that hints at what this phase will ask you to change?`,
    `What have you already noticed, half-consciously, about the ${sign} Phase without acting on it yet?`,
  ],
  // Midpoint: the issues are out in the open and the valuation is being tested.
  ({ sign, focus }) => [
    `What in the ${sign} Phase — ${focus} — has become impossible to ignore, and what is it asking you to re-weigh or re-value?`,
    "Where are you still pouring effort into something out of habit or self-worth rather than real value, and what would an honest reorientation look like?",
  ],
  // Final: resolution, completion, and what carries forward.
  ({ sign, focus }) => [
    `What in the ${sign} Phase — ${focus} — is genuinely resolving now, and what would complete it rather than merely end it?`,
    "What is finished here, and which part of this lesson do you want to carry into the next phase?",
  ],
];

/** The timing question: build-up, the far side of the date, or the quiet stretch. */
function timingQuestion({ focus, days }) {
  if (typeof days !== "number" || days < -APPROACHING_WINDOW_DAYS) {
    return `This is the quieter stretch between turning points: what is beginning to gather around ${focus}, and what is worth simply watching rather than resolving?`;
  }
  if (days > 0) {
    return "Now that the turning point has passed, what seems to have resolved at least at some level — and what is loosening that you no longer have to manage?";
  }
  return "When the demands on your attention are loudest it is easy to see only the trees and not the forest, and you still have the choice not to be swallowed by the overwhelm: what is the one issue actually in front of you, and can you work it with faith that growth, not collapse, is what is forming?";
}

/** Three questions: two for the Challenge Period you are in, one for the timing. */
function buildQuestions({ sign, focus, stageIndex, days }) {
  const stageQuestions =
    STAGE_QUESTION_BUILDERS[stageIndex] || STAGE_QUESTION_BUILDERS[0];
  return stageQuestions({ sign, focus }).concat(timingQuestion({ focus, days }));
}

/** How the turning point in view is named in the reading. */
function describeTurningPointPhrase(point) {
  if (point.type.className === "is-initiation") {
    return `an Initiation Point for the ${point.sign} Phase`;
  }
  if (point.type.className === "is-reorientation") {
    return `a Reorientation Point of the ${point.sign} Phase`;
  }
  return `a Standard Turning Point in the ${point.sign} Phase`;
}

/** Timing paragraph for where today sits on the wave around the turning point. */
function describeTiming(days) {
  if (typeof days !== "number") return null;
  if (days < -WAVE_WINDOW_DAYS) {
    return "The wave has turned to this next turning-point period, and today sits before its window: no marker is drawn on the graph yet and no challenge from this date is in view. This is the quieter stretch between turning points. Once the date comes within " +
      `${WAVE_WINDOW_DAYS} days, the marker reappears and this reading begins to track the build-up.`;
  }
  if (days <= -16) {
    return "You are still before the challenge window. Demands on your attention and energy usually begin to rise in the two weeks or so before the date, so nothing here needs to be forced yet — this is a period to watch rather than to resolve.";
  }
  if (days <= -4) {
    return "You are inside the build-up. Needs on your attention and energy are typically rising toward the date, and the issues that will come to a head are usually becoming visible. Notice what is asking for attention; it does not have to be settled yet.";
  }
  if (days <= 3) {
    return "You are on the crest — the exact turning point. This is where the challenge of the period is most concentrated and most conscious. Resolution usually begins to show once the date has passed, so hold steady rather than forcing a verdict today.";
  }
  return "The date has passed and you are in the resolution tail. Signs of resolution should be showing, and some sense of what is next may already be arriving. Look for what is loosening rather than for a new crisis.";
}

/**
 * Build the reading for the turning point the wave graph is currently centred on,
 * so the reading and the blue graph marker always advance together.
 *
 * @param {object|null} clock Result of computeLifeClock().
 * @returns {{eyebrow: string, heading: string, standfirst: string, sections: Array}|null}
 */
export function buildTurningPointReading(clock) {
  if (!clock) return null;

  const { name, nearest, activeWave, daysToActiveWave } = clock;
  const activeTp = activeWave || nearest;
  if (!activeTp) return null;

  // Everything below is keyed to the turning point itself, so the reading holds
  // steady for as long as that turning point is the one in view.
  const sign = activeTp.sign;
  const stageIndex = activeTp.index % 4;
  const phase = getPhaseText(sign);
  const challenge = getChallengePeriodText(stageIndex);
  if (!phase || !challenge) return null;

  const days =
    typeof daysToActiveWave === "number" ? daysToActiveWave : activeTp.diffDays;

  const sections = [
    {
      heading: "What This Clock Does",
      paragraphs: [
        "This reading is composed from your date of birth alone, using the 7-Year Life Clock: a 7-year cycle of twelve 7-month Zodiac Phases, each divided into four Challenge Periods — 48 turning point periods inside every cycle. Treat the dates as the rhythm of a process rather than as appointments.",
      ],
    },
    {
      heading: "The Phase You Are Moving Through",
      image: PHASE_GRAPHICS[sign]
        ? { src: PHASE_GRAPHICS[sign], alt: `${sign} Phase` }
        : null,
      paragraphs: [
        `You are moving through the ${sign} Phase of this cycle, and specifically its ${activeTp.stage}. That 7-month season sets the broad area of concern, and this is where in the process you currently stand.`,
        phase.description,
      ],
    },
    {
      heading: "The Challenge Period You Are Moving Through",
      paragraphs: [
        `${getChallengePeriodName(stageIndex)} of the ${sign} Phase — ${challenge.body}`,
      ],
    },
    {
      heading: "The Turning Point in View",
      paragraphs: [
        `The turning point this clock is following falls on ${formatDate(activeTp.date)}: ${describeTurningPointPhrase(activeTp)}. ${activeTp.type.meaning}`,
        describeTiming(days),
        "This reading stays with that turning point for as long as the blue marker on the wave graph stays with it. When the marker advances to the next turning point, this reading is replaced by the reading for that one.",
      ].filter(Boolean),
    },
    {
      heading: "Questions to Sit With",
      bullets: buildQuestions({
        sign,
        focus: phase.focus,
        stageIndex,
        days,
      }),
    },
  ];

  return {
    eyebrow: "Turning Point Reading",
    heading: `${name ? `${name}'s` : "Your"} Turning Point Reading`,
    standfirst: `The turning point in view: ${formatDate(activeTp.date)} — ${describeDayDistance(days)}.`,
    sections,
  };
}
